import React, { useCallback, useEffect, useRef, useState } from 'react';
import { estaOcupado } from '../utils/refresco';

/* Mantiene al día a las apps instaladas en el celular.

   El problema: una app agregada a la pantalla de inicio no se recarga nunca.
   Queda abierta en segundo plano y al volver a abrirla sigue corriendo el
   JavaScript con el que arrancó, que puede ser de hace días. Por eso un cambio
   publicado no se veía hasta borrar la app y volver a instalarla.

   La solución es que la app pregunte. Cada tanto, y sobre todo al volver del
   segundo plano, compara el bundle que está corriendo contra el que hay
   publicado. Si estuvo guardada un buen rato, se actualiza sola —es el caso de
   "abro la app a la mañana"—; si la estás usando, avisa y te deja elegir, que
   es lo único honesto cuando podés tener algo a medio escribir. */

const CADA = 5 * 60 * 1000;        // cada cuánto pregunta mientras la usás
const DORMIDA = 20 * 60 * 1000;    // guardada más que esto, se actualiza sola
const ESPERA = 30 * 1000;          // no preguntar dos veces seguidas
const LLAVE = 'version_recargada';  // para no recargar dos veces por lo mismo

/* Lo que está corriendo ahora, leído de las propias etiquetas de la página:
   el bundle de javascript y la hoja de estilos. Los dos llevan el hash del
   contenido, así que si cambió una coma en cualquiera de los dos, cambia.
   Mirar solo el javascript dejaba afuera los cambios de puro estilo. */
const miVersion = () => {
  const js = document.querySelector('script[src*="/static/js/main."]');
  const css = document.querySelector('link[rel="stylesheet"][href*="/static/css/main."]');
  const partes = [js?.src, css?.href].filter(Boolean).map(u => u.split('/').pop());
  return partes.length ? partes.join('+') : null;
};

async function versionPublicada() {
  try {
    const r = await fetch('/api/version', { cache: 'no-store' });
    if (!r.ok) return null;
    const { version } = await r.json();
    return version || null;
  } catch { return null; }   // sin internet no hay nada que actualizar
}

/* Recargar sola es cómodo y peligroso: si por lo que sea la versión publicada
   no coincidiera nunca con la que llega al navegador, la app se recargaría en
   loop y quedaría inusable. Por eso cada versión se recarga sola una sola vez;
   si después de eso sigue sin coincidir, se muestra el cartel y decide la
   persona. */
function yaSeRecargo(v) {
  try { return sessionStorage.getItem(LLAVE) === v; } catch { return false; }
}
function anotarRecarga(v) {
  try { sessionStorage.setItem(LLAVE, v); } catch { /* modo privado */ }
}

export default function Actualizaciones() {
  const [hayNueva, setHayNueva] = useState(false);
  const mia = useRef(miVersion());
  const oculta = useRef(0);
  const ultima = useRef(0);

  const revisar = useCallback(async (puedeRecargar = false) => {
    if (Date.now() - ultima.current < ESPERA) return;
    ultima.current = Date.now();
    const v = await versionPublicada();
    if (!v || !mia.current || v === mia.current) return;
    if (puedeRecargar && !estaOcupado() && !yaSeRecargo(v)) {
      anotarRecarga(v);
      return window.location.reload();
    }
    setHayNueva(true);
  }, []);

  useEffect(() => {
    const reloj = setInterval(() => { if (!document.hidden) revisar(); }, CADA);

    /* Tres eventos para lo mismo porque ninguno solo alcanza: iOS en modo app
       a veces no dispara visibilitychange y sí pageshow, y al revés. */
    const alVolver = () => {
      if (document.hidden) { oculta.current = Date.now(); return; }
      const dormida = oculta.current ? Date.now() - oculta.current : 0;
      oculta.current = 0;
      revisar(dormida >= DORMIDA);
    };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('pageshow', alVolver);
    window.addEventListener('focus', alVolver);
    return () => {
      clearInterval(reloj);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('pageshow', alVolver);
      window.removeEventListener('focus', alVolver);
    };
  }, [revisar]);

  if (!hayNueva) return null;
  return (
    <div className="aviso-version" role="status">
      <span>Hay una versión nueva.</span>
      <button onClick={() => window.location.reload()}>Actualizar</button>
      <button className="despues" onClick={() => setHayNueva(false)} aria-label="Cerrar">×</button>
    </div>
  );
}
